import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { HeaderV2, FooterV2 } from '../../components/layout';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/authErrors';
import { API_BASE_URL } from '../../config/api';
import { login } from '../../store/slices/authSlice';

const shouldOfferAccountCreation = () => {
  const hasToken =
    localStorage.getItem('accessToken') || localStorage.getItem('userToken');
  if (hasToken) return false;
  if (localStorage.getItem('booking.accountCreated') === 'true') return false;
  return true;
};

const BookingConfirmationPageV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const bookingRef = state?.bookingRef;
  const pin = state?.pin;
  const { form } = state || {};

  const [showAccountModal, setShowAccountModal] = useState(shouldOfferAccountCreation);
  const [accountTab, setAccountTab] = useState('login');
  const [email, setEmail] = useState(form?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [accountError, setAccountError] = useState('');

  useEffect(() => {
    if (!bookingRef || pin == null) {
      navigate('/v2', { replace: true });
    }
  }, [bookingRef, pin, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowAccountModal(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (form?.email) {
      setEmail(form.email);
    }
  }, [form?.email]);

  if (!bookingRef || pin == null) {
    return null;
  }

  const sendReceipt = async (includeAccountInfo = false) => {
    try {
      const emailData = {
        access_key: '139d6700-65b6-4faf-8b1b-016897b4e9b4',
        subject: `Booking Confirmation - ${bookingRef}`,
        from_name: 'Umrah Booking System',
        email: form?.email,
        message: `
          Dear ${form?.name},
          
          Your booking has been confirmed!
          
          Booking Reference: ${bookingRef}
          Secure PIN: ${pin}
          
          Package Details:
          Name: ${form?.name}
          Email: ${form?.email}
          Phone: ${form?.phone}
          
          ${includeAccountInfo ? 'Your account has been created. You can now login with your email and password.' : 'To create an account and track your booking, visit our website.'}
          
          Thank you for choosing our services!
        `,
      };

      await axios.post('https://api.web3forms.com/submit', emailData);

      const whatsappMessage = `Booking Confirmed!%0ARef: ${bookingRef}%0APIN: ${pin}%0A%0AThank you ${form?.name}!`;
      const whatsappNumber = '923001234567';
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Failed to send receipt:', error);
    }
  };

  const handleLogin = async () => {
    setAccountError('');
    if (!email?.trim() || !password) {
      setAccountError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      await dispatch(login({ email: email.trim(), password })).unwrap();
      localStorage.setItem('booking.accountCreated', 'true');
      setShowAccountModal(false);
      alert('Signed in successfully. You can manage bookings from your dashboard.');
    } catch (e) {
      setAccountError(getApiErrorMessage(e, 'Login failed. Check your email and password.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setAccountError('');
    if (!password || password !== confirmPassword) {
      setAccountError('Passwords do not match');
      return;
    }
    if (!email?.trim()) {
      setAccountError('Email is required to create an account.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/register`, {
        email: email.trim(),
        password,
        confirmPassword,
        phone: form?.phone,
        phoneCountryCode: form?.phone ? '+92' : undefined,
      });

      setAccountCreated(true);
      localStorage.setItem('booking.accountCreated', 'true');
      await sendReceipt(true);
      setShowAccountModal(false);
      alert('Account created successfully! You can sign in with this email and password.');
    } catch (e) {
      const msg = getApiErrorMessage(e, e.message);
      if (msg.toLowerCase().includes('already') || msg.toLowerCase().includes('exist')) {
        setAccountError('This email is already registered. Use the Sign in tab.');
        setAccountTab('login');
      } else {
        setAccountError('Failed to create account: ' + msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    await sendReceipt(false);
    setShowAccountModal(false);
    setLoading(false);
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'success.main' }}>
            ✓ Booking Confirmed!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your booking has been placed successfully.
          </Typography>

          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Booking Reference</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{bookingRef}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Secure PIN</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{pin}</Typography>
          </Paper>

          {isAuthenticated && (
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mb: 2 }}>
              You are signed in — manage this booking from your dashboard.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {form?.email ? `We will contact you at ${form.email}` : 'Our team will contact you shortly.'}
          </Typography>
          {accountCreated && (
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mb: 2 }}>
              ✓ Account created successfully! You can now log in anytime.
            </Typography>
          )}
          <Button variant="contained" onClick={() => navigate(isAuthenticated ? '/v2/dashboard' : '/v2')}>
            {isAuthenticated ? 'Go to Dashboard' : 'Back to Home'}
          </Button>
        </Paper>
      </Container>

      {showAccountModal && !isAuthenticated && (
        <Dialog open onClose={() => setShowAccountModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Link your booking to an account (optional)</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Tabs
              value={accountTab}
              onChange={(_, v) => {
                setAccountTab(v);
                setAccountError('');
              }}
              sx={{ mb: 2 }}
            >
              <Tab label="I already have an account" value="login" />
              <Tab label="Create new account" value="register" />
            </Tabs>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              helperText={form?.email ? 'From your booking' : undefined}
            />

            {accountTab === 'login' ? (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Sign in to see this booking in your dashboard. Your reference and PIN still work without an account.
                </Typography>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Minimum 8 characters"
                />
                <TextField
                  fullWidth
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </>
            )}

            {accountError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {accountError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSkip} disabled={loading}>
              Skip
            </Button>
            {accountTab === 'login' ? (
              <Button onClick={handleLogin} variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Sign in'}
              </Button>
            ) : (
              <Button onClick={handleCreateAccount} variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Create account'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      <FooterV2 />
    </Box>
  );
};

export default BookingConfirmationPageV2;
